import React, { useState } from 'react';
import { FeatureItem } from './feature-item';
import { QaCard } from './qa-card';
import { SectionHeader } from './section-header';
import { useScrollReveal } from './use-scroll-reveal';

export interface FeaturePanelProps {
  id: string;
  tabId: string;
  title: string;
  subtitle: string;
  titleTag?: 'h1' | 'h2' | 'h3';
  subtitleColor?: 'default' | 'primary' | 'secondary' | 'tertiary' | 'muted' | 'white' | 'slate';
  features: {
    icon: React.ReactNode;
    title: string;
    description: string;
  }[];
  qaItems?: {
    question: string;
    answer: string;
  }[];
  qaTitle?: string;
}

export function FeaturePanel({
  id,
  tabId,
  title,
  subtitle,
  titleTag,
  subtitleColor,
  features,
  qaItems,
  qaTitle,
}: FeaturePanelProps) {
  const qaRevealRef = useScrollReveal();
  const [flippedIndex, setFlippedIndex] = useState<number | null>(null);

  const handleFlip = (index: number) => {
    setFlippedIndex(prev => (prev === index ? null : index));
  };

  return (
    <div role="tabpanel" id={id} aria-labelledby={tabId} className="animate-fade-in">
      <SectionHeader
        title={title}
        subtitle={subtitle}
        titleTag={titleTag}
        subtitleColor={subtitleColor}
      />

      <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr] gap-8 text-left items-start">
        {/* Left: Feature Items */}
        <div className="grid grid-cols-1 gap-4">
          {features.map(feature => (
            <div key={feature.title} className="w-full h-full flex flex-col">
              <FeatureItem
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
                layout="horizontal"
                variant="neutral"
                isCard
                minHeight="120px"
                removeIconBackground
                iconSize="w-16 h-16 md:w-20 md:h-20 shrink-0"
              />
            </div>
          ))}
        </div>

        {/* Right: FAQ (sticky-note 2-col grid) */}
        {qaItems && qaItems.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-2xl font-extrabold tracking-tight text-slate-900 text-center ">
              {qaTitle || 'FAQ'}
            </h3>
            <p className="text-center text-slate-600 text-xs pb-2 flex items-center justify-center gap-2">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-slate-900 shrink-0">
                <svg
                  className="w-3.5 h-3.5 text-tertiary"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 4l7.07 17 2.51-7.39L21 11.07z" />
                </svg>
              </span>
              Select each card to explore more
            </p>
            <div
              ref={qaRevealRef}
              className="grid grid-cols-1 sm:grid-cols-2 gap-5 justify-items-center"
            >
              {qaItems.map((item, index) => (
                <div key={item.question} className="scroll-reveal w-full max-w-[200px]">
                  <QaCard
                    index={index}
                    idPrefix={id}
                    question={item.question}
                    answer={item.answer}
                    isFlipped={flippedIndex === index}
                    onFlip={() => handleFlip(index)}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
